/**
 * Unified viewport height utilities for consistent layout across the app
 */

/**
 * Gets the optimal viewport height for layout calculations
 * Uses visual viewport for mobile to exclude browser UI, falls back to innerHeight
 */
export const getViewportHeight = (): number => {
  // Use visual viewport if available (excludes browser UI on mobile)
  if (window.visualViewport) {
    return window.visualViewport.height;
  }
  // Fallback to innerHeight
  return window.innerHeight;
};

/**
 * Updates the CSS custom property for viewport height
 * Used for CSS calc() expressions and mobile browser compatibility
 */
export const updateViewportHeightCSS = (): void => {
  const height = getViewportHeight();
  const vh = height * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--viewport-height', '100dvh'); // Use 100dvh directly in CSS
};

/**
 * Gets the total layout height excluding controls bar
 * @param controlsHeight Height of the controls bar in pixels
 */
export const getContentHeight = (controlsHeight: number): number => {
  const viewportHeight = getViewportHeight();
  return Math.max(0, viewportHeight - controlsHeight);
};

/**
 * Sets up viewport height monitoring for responsive behavior
 * Updates CSS custom properties on viewport changes
 */
export const setupViewportHeightMonitoring = (): (() => void) => {
  updateViewportHeightCSS();
  
  const handleResize = () => {
    updateViewportHeightCSS();
  };
  
  const handleOrientationChange = () => {
    setTimeout(() => updateViewportHeightCSS(), 100);
  };
  
  // Visual Viewport API events (more precise than window resize)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleResize);
  }
  
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleOrientationChange);
  
  // Cleanup function
  return () => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleResize);
    }
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleOrientationChange);
  };
};

/**
 * Constrains scrolling to prevent overscroll beyond content
 * Ensures user can't scroll past the controls bar
 */
export const constrainScrolling = (): void => {
  const body = document.body;
  const html = document.documentElement;
  
  // Prevent overscroll behavior
  body.style.overscrollBehavior = 'none';
  html.style.overscrollBehavior = 'none';
  
  // Set maximum scroll height to prevent scrolling beyond content
  const maxHeight = getViewportHeight();
  body.style.maxHeight = `${maxHeight}px`;
  html.style.maxHeight = `${maxHeight}px`;
};
